import { useState, type FormEvent, type ReactNode } from "react";
import { apiFetch, ApiError } from "../lib/apiFetch";
import {
  parseDateInputValue,
  requestCallMaxDate,
  requestCallMinDate,
  validateRequestedDate,
} from "../lib/requestCallDates";
import type { CallRequestCreate, CallRequestTimeWindow } from "../types/api";
import { Input } from "./Input";
import { Select } from "./Select";
import { Checkbox } from "./Checkbox";
import { Button } from "./Button";

/** Prefill for the identity fields (member/partner portals). Every field stays
 *  editable, since the caller may want the call on a different number. */
export interface RequestCallPrefill {
  firstName?: string | undefined;
  lastName?: string | undefined;
  phone?: string | undefined;
}

/** All user-visible strings of the request-a-call tab. The customer app passes
 *  localized copy; member/partner rely on the English defaults. The modal title
 *  and tab labels live on `ContactUsModalCopy`. */
export interface RequestCallCopy {
  intro: string;
  whenLegend: string;
  dateLabel: string;
  windowLabel: string;
  windowPlaceholder: string;
  windowMorning: string;
  windowAfternoon: string;
  windowLateAfternoon: string;
  firstNameLabel: string;
  lastNameLabel: string;
  phoneLabel: string;
  zipLabel: string;
  consentLabel: string;
  submitLabel: string;
  submittingLabel: string;
  cancelLabel: string;
  /** `{date}` is replaced with the requested date (formatted per `dateLocale`). */
  successBody: string;
  doneLabel: string;
  /** Missing/unparseable date, or one outside tomorrow..+30 days. */
  dateError: string;
  /** In-range date that falls on a Saturday/Sunday. */
  dateWeekendError: string;
  windowRequiredError: string;
  firstNameRequiredError: string;
  lastNameRequiredError: string;
  phoneRequiredError: string;
  zipError: string;
  consentRequiredError: string;
  /** 422 (or any other unexpected failure). */
  submitError: string;
  /** 429: the caller already requested a call recently. */
  rateLimitError: string;
}

const DEFAULT_REQUEST_CALL_COPY: RequestCallCopy = {
  intro:
    "Have questions? You can request a call from a DearBill agent. Fill out this form to have us call you.",
  whenLegend: "When would you like us to call you?",
  dateLabel: "Date",
  windowLabel: "Time",
  windowPlaceholder: "Select a time",
  windowMorning: "Morning: 9 AM to noon ET",
  windowAfternoon: "Afternoon: Noon to 3 PM ET",
  windowLateAfternoon: "Late afternoon: 3 PM to 7 PM ET",
  firstNameLabel: "First name",
  lastNameLabel: "Last name",
  phoneLabel: "Phone number",
  zipLabel: "ZIP code",
  consentLabel: "By checking this box, you agree to have a DearBill agent contact you.",
  submitLabel: "Send",
  submittingLabel: "Sending…",
  cancelLabel: "Cancel",
  successBody: "Thanks — a DearBill agent will call you on {date} in your chosen window.",
  doneLabel: "Done",
  dateError: "Choose a weekday between tomorrow and 30 days from now.",
  dateWeekendError: "We call Monday through Friday — please choose a weekday.",
  windowRequiredError: "Select a time.",
  firstNameRequiredError: "Enter your first name.",
  lastNameRequiredError: "Enter your last name.",
  phoneRequiredError: "Enter your phone number.",
  zipError: "Enter a 5-digit ZIP code.",
  consentRequiredError: "Please check the box so a DearBill agent can contact you.",
  submitError: "Please check your details and try again.",
  rateLimitError: "You’ve recently requested a call — please try again later.",
};

export interface RequestCallFormProps {
  onClose: () => void;
  /** Which SPA the request came from. */
  source: CallRequestCreate["source"];
  /** `import.meta.env.VITE_API_BASE_URL ?? ""` from the calling SPA. */
  baseUrl: string;
  /** Identity prefill; everything stays editable. */
  prefill?: RequestCallPrefill | undefined;
  /** Localized overrides; anything omitted falls back to the English defaults. */
  copy?: Partial<RequestCallCopy> | undefined;
  /** BCP-47 locale for the success-panel date. Defaults to `"en"`. */
  dateLocale?: string | undefined;
}

/** `| undefined` throughout for `exactOptionalPropertyTypes`, because the date
 *  error is cleared in place on change. */
interface FormErrors {
  date?: string | undefined;
  window?: string | undefined;
  firstName?: string | undefined;
  lastName?: string | undefined;
  phone?: string | undefined;
  zip?: string | undefined;
  consent?: string | undefined;
}

const ZIP_RE = /^\d{5}$/;

const WINDOW_VALUES = ["morning", "afternoon", "late_afternoon"] as const;

function isTimeWindow(value: string): value is CallRequestTimeWindow {
  return WINDOW_VALUES.some((w) => w === value);
}

/**
 * The "Request Call" tab of `ContactUsModal`, which posts a `CallRequestCreate` to the public endpoint.
 *
 * On success it shows a confirmation naming the requested date. The form
 * mounts fresh each time the modal opens and on tab switch, so its fields reset
 * from `prefill` without a reset effect.
 */
export function RequestCallForm({
  onClose,
  source,
  baseUrl,
  prefill,
  copy: copyOverrides,
  dateLocale = "en",
}: RequestCallFormProps): ReactNode {
  const copy: RequestCallCopy = { ...DEFAULT_REQUEST_CALL_COPY, ...copyOverrides };
  // Bounds are computed once per mount (the form remounts per open), so the attributes stay stable.
  const [minDate] = useState(() => requestCallMinDate());
  const [maxDate] = useState(() => requestCallMaxDate());
  const [date, setDate] = useState("");
  const [timeWindow, setTimeWindow] = useState<"" | CallRequestTimeWindow>("");
  const [firstName, setFirstName] = useState(prefill?.firstName ?? "");
  const [lastName, setLastName] = useState(prefill?.lastName ?? "");
  const [phone, setPhone] = useState(prefill?.phone ?? "");
  const [zip, setZip] = useState("");
  const [consent, setConsent] = useState(false);
  const [website, setWebsite] = useState(""); // honeypot; humans leave it empty
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // The requested date once accepted; it doubles as the "submitted" flag.
  const [submittedDate, setSubmittedDate] = useState<string | null>(null);

  const dateErrorFor = (value: string): string | undefined => {
    const issue = validateRequestedDate(value);
    if (issue === null) return undefined;
    return issue === "weekend" ? copy.dateWeekendError : copy.dateError;
  };

  if (submittedDate !== null) {
    const parsed = parseDateInputValue(submittedDate);
    const display = parsed
      ? parsed.toLocaleDateString(dateLocale, { weekday: "long", month: "long", day: "numeric" })
      : submittedDate;
    return (
      <div>
        <p
          role="status"
          className="rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800"
        >
          {copy.successBody.replace("{date}", display)}
        </p>
        <div className="mt-6 flex justify-end">
          <Button onClick={onClose}>{copy.doneLabel}</Button>
        </div>
      </div>
    );
  }

  const onSubmit = (e: FormEvent): void => {
    e.preventDefault();
    const fn = firstName.trim();
    const ln = lastName.trim();
    const ph = phone.trim();
    const z = zip.trim();
    const next: FormErrors = {};
    const dateErr = dateErrorFor(date);
    if (dateErr) next.date = dateErr;
    if (!timeWindow) next.window = copy.windowRequiredError;
    if (!fn) next.firstName = copy.firstNameRequiredError;
    if (!ln) next.lastName = copy.lastNameRequiredError;
    if (!ph) next.phone = copy.phoneRequiredError;
    if (!ZIP_RE.test(z)) next.zip = copy.zipError;
    if (!consent) next.consent = copy.consentRequiredError;
    setErrors(next);
    setSubmitError(null);
    // `!timeWindow` is already covered by `next.window`; it repeats here for type narrowing.
    if (!timeWindow || Object.keys(next).length > 0) return;

    setSubmitting(true);
    const payload: CallRequestCreate = {
      source,
      firstName: fn,
      lastName: ln,
      phone: ph,
      zip: z,
      requestedDate: date,
      timeWindow,
      website,
    };
    apiFetch(
      "/api/contact/call-requests",
      { method: "POST", body: payload as unknown as Record<string, unknown> },
      { baseUrl },
    )
      .then(() => setSubmittedDate(date))
      .catch((err: unknown) => {
        setSubmitting(false);
        setSubmitError(
          err instanceof ApiError && err.status === 429 ? copy.rateLimitError : copy.submitError,
        );
      });
  };

  return (
    <form className="space-y-4" onSubmit={onSubmit} noValidate>
      <p className="text-sm text-slate-600">{copy.intro}</p>

      <fieldset>
        <legend className="text-sm font-medium text-slate-800">{copy.whenLegend}</legend>
        <div className="mt-2 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input
            label={copy.dateLabel}
            type="date"
            required
            min={minDate}
            max={maxDate}
            value={date}
            error={errors.date}
            onChange={(e) => {
              const value = e.target.value;
              setDate(value);
              // A native date input can't gray out weekends, so validate on
              // change to give a Saturday pick immediate feedback.
              setErrors((prev) => ({ ...prev, date: value ? dateErrorFor(value) : undefined }));
            }}
          />
          <Select
            label={copy.windowLabel}
            required
            value={timeWindow}
            error={errors.window}
            onChange={(e) => {
              setTimeWindow(isTimeWindow(e.target.value) ? e.target.value : "");
            }}
          >
            <option value="">{copy.windowPlaceholder}</option>
            <option value="morning">{copy.windowMorning}</option>
            <option value="afternoon">{copy.windowAfternoon}</option>
            <option value="late_afternoon">{copy.windowLateAfternoon}</option>
          </Select>
        </div>
      </fieldset>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Input
          label={copy.firstNameLabel}
          autoComplete="given-name"
          required
          value={firstName}
          error={errors.firstName}
          onChange={(e) => setFirstName(e.target.value)}
        />
        <Input
          label={copy.lastNameLabel}
          autoComplete="family-name"
          required
          value={lastName}
          error={errors.lastName}
          onChange={(e) => setLastName(e.target.value)}
        />
      </div>
      <Input
        label={copy.phoneLabel}
        type="tel"
        autoComplete="tel"
        required
        value={phone}
        error={errors.phone}
        onChange={(e) => setPhone(e.target.value)}
      />
      <Input
        label={copy.zipLabel}
        inputMode="numeric"
        autoComplete="postal-code"
        maxLength={5}
        required
        value={zip}
        error={errors.zip}
        onChange={(e) => setZip(e.target.value)}
      />
      <Checkbox
        label={copy.consentLabel}
        required
        checked={consent}
        error={errors.consent}
        onChange={(e) => setConsent(e.target.checked)}
      />

      {/* Honeypot: off-screen, non-focusable, hidden from AT. A filled value is a bot. */}
      <input
        type="text"
        name="website"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        value={website}
        onChange={(e) => setWebsite(e.target.value)}
        className="pointer-events-none absolute -left-[9999px] top-0 h-0 w-0 overflow-hidden opacity-0"
      />

      {submitError && (
        <p
          role="alert"
          className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
        >
          {submitError}
        </p>
      )}

      <div className="flex justify-end gap-3 pt-2">
        <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>
          {copy.cancelLabel}
        </Button>
        <Button type="submit" disabled={submitting}>
          {submitting ? copy.submittingLabel : copy.submitLabel}
        </Button>
      </div>
    </form>
  );
}
